import React from 'react';
import { getDynamicPlayerHeadshot } from '../utils/playerData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from 'recharts';

const PlayerStatsModal = ({ pick, game, isOpen, onClose }) => {
  const [playerHeadshot, setPlayerHeadshot] = React.useState(null);

  React.useEffect(() => {
    if (isOpen && pick) {
      const loadHeadshot = async () => {
        const league = pick.league || game?.league || 'nba';
        const headshot = await getDynamicPlayerHeadshot(pick.player, pick.player_id, league, pick.espn_id);
        setPlayerHeadshot(headshot);
      };
      loadHeadshot();
    }
  }, [isOpen, pick, game]);

  if (!isOpen || !pick) return null;

  const getPlayerInitials = (playerName) => {
    const names = playerName.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return playerName.substring(0, 2).toUpperCase();
  };

  // Helper function to prepare chart data with color coding
  const prepareChartData = (values, lineValue) => {
    if (!values || !lineValue) return [];
    return values.map((value, index) => ({
      game: `G${index + 1}`,
      value: value,
      line: parseFloat(lineValue),
      color: value > parseFloat(lineValue) ? '#00ff00' : '#ff4444'
    }));
  };

  // Prepare data for H2H chart
  const h2hData = prepareChartData(pick.individual_game_values, pick.line_value);

  // Prepare data for L5 chart
  const l5Data = prepareChartData(pick.last_5_game_values, pick.line_value);

  // Prepare data for L10 chart
  const l10Data = prepareChartData(pick.last_10_game_values, pick.line_value);

  // Prepare data for L20 chart
  const l20Data = prepareChartData(pick.last_20_game_values, pick.line_value);

  // Prepare data for L50 chart
  const l50Data = prepareChartData(pick.last_50_game_values, pick.line_value);

  // Prepare hit rate comparison data
  const hitRateData = [
    {
      period: 'L5',
      hitRate: pick.hit_rate_last_5 || 0,
      total: pick.total_last_5 || 0
    },
    {
      period: 'L10',
      hitRate: pick.hit_rate_last_10 || 0,
      total: pick.total_last_10 || 0
    },
    {
      period: 'L20',
      hitRate: pick.hit_rate_last_20 || 0,
      total: pick.total_last_20 || 0
    },
    {
      period: 'H2H',
      hitRate: pick.hit_rate_vs_team || 0,
      total: pick.individual_game_values?.length || 0
    },
    {
      period: 'L50',
      hitRate: pick.hit_rate_last_50 || 0,
      total: pick.total_last_50 || 0
    }
  ].filter(item => item.total > 0);

  const formatStatType = (prop) => {
    if (!prop) return 'Unknown';
    const parts = prop.split(' ');
    return parts[0] || 'Unknown';
  };

  const statType = formatStatType(pick.prop);

  // Calculate mathematical projection based on all available data
  const calculateProjection = () => {
    const lineValue = parseFloat(pick.line_value);
    if (!lineValue || isNaN(lineValue)) return null;

    let projection = 0;
    let totalWeight = 0;

    // 1. Recent Performance (weighted by recency)
    const recentWeights = [
      { data: pick.last_5_game_values, weight: 0.4, name: 'L5' },
      { data: pick.last_10_game_values, weight: 0.3, name: 'L10' },
      { data: pick.last_20_game_values, weight: 0.2, name: 'L20' },
      { data: pick.last_50_game_values, weight: 0.1, name: 'L50' }
    ];

    recentWeights.forEach(({ data, weight }) => {
      if (data && data.length > 0) {
        const avg = data.reduce((sum, val) => sum + parseFloat(val), 0) / data.length;
        // Adjust weight based on sample size (more games = more confidence)
        const sampleWeight = Math.min(1, data.length / 10); // Full weight at 10+ games
        projection += avg * weight * sampleWeight;
        totalWeight += weight * sampleWeight;
      }
    });

    // 2. H2H Performance (vs opponent) - higher weight if we have good sample
    if (pick.individual_game_values && pick.individual_game_values.length > 0) {
      const h2hAvg = pick.individual_game_values.reduce((sum, val) => sum + parseFloat(val), 0) / pick.individual_game_values.length;
      const h2hWeight = Math.min(0.4, pick.individual_game_values.length * 0.05); // Max 0.4 weight, scales with sample size
      projection += h2hAvg * h2hWeight;
      totalWeight += h2hWeight;
    }

    // 3. Historical Average as baseline (if we have limited recent data)
    if (pick.historical_avg && totalWeight < 0.5) {
      const baselineWeight = 0.3 * (1 - totalWeight);
      projection += parseFloat(pick.historical_avg) * baselineWeight;
      totalWeight += baselineWeight;
    }

    // Normalize by total weight
    if (totalWeight > 0) {
      projection = projection / totalWeight;
    } else if (pick.historical_avg) {
      // Fallback to historical average if no other data
      projection = parseFloat(pick.historical_avg);
    } else {
      return null;
    }

    return {
      value: Math.round(projection * 10) / 10, // Round to 1 decimal
      vsLine: Math.round((projection - lineValue) * 10) / 10,
      vsLinePercent: Math.round(((projection - lineValue) / lineValue) * 1000) / 10
    };
  };

  const projection = calculateProjection();

  return (
    <div className="player-stats-modal-overlay" onClick={onClose}>
      <div className="player-stats-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="player-stats-modal-close" onClick={onClose}>
          ×
        </button>

        {/* Header with Player Photo and Info */}
        <div className="player-stats-modal-header">
          <div className="player-stats-photo-container">
            {playerHeadshot ? (
              <img 
                src={playerHeadshot} 
                alt={pick.player}
                className="player-stats-photo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="player-stats-initials" 
              style={{ display: playerHeadshot ? 'none' : 'flex' }}
            >
              {getPlayerInitials(pick.player)}
            </div>
          </div>
          <div className="player-stats-header-info">
            <h2 className="player-stats-name">{pick.player}</h2>
            <p className="player-stats-prop">{pick.prop}</p>
            <p className="player-stats-game">{game?.game || pick.game}</p>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="player-stats-summary">
          <div className="stat-summary-item">
            <span className="stat-summary-label">Historical Avg:</span>
            <span className="stat-summary-value">{pick.historical_avg || 'N/A'}</span>
          </div>
          <div className="stat-summary-item">
            <span className="stat-summary-label">Line:</span>
            <span className="stat-summary-value">{pick.line_value || 'N/A'}</span>
          </div>
          <div className="stat-summary-item">
            <span className="stat-summary-label">Edge Score:</span>
            <span className="stat-summary-value">{pick.edge_score || 'N/A'}</span>
          </div>
          <div className="stat-summary-item">
            <span className="stat-summary-label">Projection:</span>
            <span className={`stat-summary-value ${projection ? (projection.vsLine > 0 ? 'projection-over' : projection.vsLine < 0 ? 'projection-under' : 'projection-neutral') : ''}`}>
              {projection ? (
                <>
                  {projection.value} 
                  {projection.vsLine !== 0 && (
                    <span style={{ fontSize: '0.85em', marginLeft: '8px', opacity: 0.8 }}>
                      ({projection.vsLine > 0 ? '+' : ''}{projection.vsLinePercent}% vs line)
                    </span>
                  )}
                </>
              ) : 'N/A'}
            </span>
          </div>
        </div>

        {/* Hit Rate Comparison Chart */}
        {hitRateData.length > 0 && (
          <div className="player-stats-chart-section">
            <h3 className="chart-title">Hit Rate by Period</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hitRateData}>
                <XAxis dataKey="period" />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  formatter={(value, name) => [`${value.toFixed(1)}%`, 'Hit Rate']}
                  labelFormatter={(label) => `Period: ${label}`}
                />
                <Legend />
                <Bar dataKey="hitRate" fill="#ff751f" name="Hit Rate %" />
                <ReferenceLine y={50} stroke="#666" strokeDasharray="3 3" label="50%" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* H2H Chart */}
        {h2hData.length > 0 && (
          <div className="player-stats-chart-section">
            <h3 className="chart-title">Head-to-Head vs Opponent ({h2hData.length} games)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={h2hData}>
                <XAxis dataKey="game" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, statType]}
                  labelFormatter={(label) => `Game: ${label}`}
                />
                <Legend />
                <ReferenceLine 
                  y={pick.line_value} 
                  stroke="#00ff00" 
                  strokeDasharray="3 3" 
                  label={{ value: `Line: ${pick.line_value}`, position: "top" }}
                />
                <Bar dataKey="value" name={statType}>
                  {h2hData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* L5 Chart */}
        {l5Data.length > 0 && (
          <div className="player-stats-chart-section">
            <h3 className="chart-title">Last 5 Games</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={l5Data}>
                <XAxis dataKey="game" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, statType]}
                  labelFormatter={(label) => `Game: ${label}`}
                />
                <Legend />
                <ReferenceLine 
                  y={pick.line_value} 
                  stroke="#00ff00" 
                  strokeDasharray="3 3" 
                  label={{ value: `Line: ${pick.line_value}`, position: "top" }}
                />
                <Bar dataKey="value" name={statType}>
                  {l5Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* L10 Chart */}
        {l10Data.length > 0 && (
          <div className="player-stats-chart-section">
            <h3 className="chart-title">Last 10 Games</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={l10Data}>
                <XAxis dataKey="game" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, statType]}
                  labelFormatter={(label) => `Game: ${label}`}
                />
                <Legend />
                <ReferenceLine 
                  y={pick.line_value} 
                  stroke="#00ff00" 
                  strokeDasharray="3 3" 
                  label={{ value: `Line: ${pick.line_value}`, position: "top" }}
                />
                <Bar dataKey="value" name={statType}>
                  {l10Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* L20 Chart */}
        {l20Data.length > 0 && (
          <div className="player-stats-chart-section">
            <h3 className="chart-title">Last 20 Games</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={l20Data}>
                <XAxis dataKey="game" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, statType]}
                  labelFormatter={(label) => `Game: ${label}`}
                />
                <Legend />
                <ReferenceLine 
                  y={pick.line_value} 
                  stroke="#00ff00" 
                  strokeDasharray="3 3" 
                  label={{ value: `Line: ${pick.line_value}`, position: "top" }}
                />
                <Bar dataKey="value" name={statType}>
                  {l20Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* L50 Chart */}
        {l50Data.length > 0 && (
          <div className="player-stats-chart-section">
            <h3 className="chart-title">Last 50 Games</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={l50Data}>
                <XAxis dataKey="game" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, statType]}
                  labelFormatter={(label) => `Game: ${label}`}
                />
                <Legend />
                <ReferenceLine 
                  y={pick.line_value} 
                  stroke="#00ff00" 
                  strokeDasharray="3 3" 
                  label={{ value: `Line: ${pick.line_value}`, position: "top" }}
                />
                <Bar dataKey="value" name={statType}>
                  {l50Data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerStatsModal;

